import { useParams, Link } from "react-router-dom";
import Seo from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import { useSettings } from "../lib/hooks";

const PAGES = {
  "privacy-policy": {
    title: "Privacy Policy",
    body: [
      ["Who we are", "NOOR Design & Build Ltd (\u201cwe\u201d, \u201cus\u201d) operates www.noordb.co.uk. We are the data controller for personal information collected through this website. You can contact us at info@noordb.co.uk or on 07782 760 279."],
      ["What we collect", "When you submit an enquiry we collect your name, email address, telephone number, project address or postcode, project details, investment range and any photographs or drawings you choose to upload. We also collect basic technical information (such as browser type) needed to operate the site."],
      ["How we use it", "We use your information solely to respond to your enquiry, assess project suitability, arrange consultations and site visits, and — where a project proceeds — to prepare proposals and deliver our services. We do not sell your information, and we do not send marketing communications without your consent."],
      ["Lawful basis", "We process enquiry information on the basis of legitimate interest (responding to your request) and, where a project proceeds, contractual necessity. Where analytics or marketing cookies are used, the lawful basis is your consent, given through our cookie controls."],
      ["Who we share it with", "Your information is shared only with the people and systems needed to respond to your enquiry (for example our email provider). We do not share your details with third parties for their own marketing purposes."],
      ["How long we keep it", "Enquiry information is retained for as long as needed to handle your enquiry and for a reasonable period afterwards in case you return to us, after which it is deleted. You may ask us to delete it sooner at any time."],
      ["Your rights", "Under UK GDPR you have the right to access, correct, delete or restrict the processing of your personal data, and to object to processing. To exercise any of these rights, contact info@noordb.co.uk. You also have the right to complain to the Information Commissioner\u2019s Office (ico.org.uk)."],
      ["Security", "Enquiry data is stored securely with access restricted to authorised team members. This website is served over HTTPS."],
      ["Changes", "We may update this policy from time to time. The current version will always be published on this page."],
    ],
  },
  "cookie-policy": {
    title: "Cookie Policy",
    body: [
      ["What cookies we use", "This website uses a small number of strictly necessary cookies (required for the site to function, including remembering your cookie choice). With your consent, we may also use analytics cookies (such as Google Analytics 4) and marketing cookies (such as the Meta Pixel) to understand how the site is used and to measure the effectiveness of our advertising."],
      ["Your choice", "When you first visit, you can accept all cookies or reject non-essential cookies using the consent banner. Non-essential analytics and marketing cookies are never set without your consent. You can change your choice at any time by clearing your browser\u2019s stored data for this site and revisiting."],
      ["Managing cookies", "You can also control cookies through your browser settings. Blocking some cookies may affect how the website functions."],
      ["Questions", "If you have questions about how we use cookies, contact info@noordb.co.uk."],
    ],
  },
  "terms-of-use": {
    title: "Terms of Website Use",
    body: [
      ["About this website", "This website is operated by NOOR Design & Build Ltd. By using it you accept these terms. The site provides general information about our services; it does not constitute professional advice or a quotation."],
      ["Accuracy", "We work to keep information accurate and up to date, but content is provided without warranty. Any guidance on planning, Building Regulations or costs is general in nature — the position for your property may differ, and project-specific advice is given during consultation."],
      ["Imagery", "Photographs in our Projects section depict genuine NOOR Design & Build projects, photographed on completion. Decorative imagery used elsewhere on this site (such as service and article imagery) is representative of our style of work and does not depict specific NOOR projects."],
      ["Intellectual property", "All content on this website belongs to or is licensed to NOOR Design & Build Ltd and may not be reproduced without permission."],
      ["Liability", "Nothing in these terms excludes liability that cannot be excluded by law. Otherwise, we are not liable for loss arising from use of this website or reliance on its content."],
      ["Governing law", "These terms are governed by the laws of England and Wales."],
      ["Contact", "Questions about these terms: info@noordb.co.uk."],
    ],
  },
};

export default function Legal({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const { data: settings } = useSettings();
  const page = PAGES[slug];

  if (!page) {
    return (
      <div className="min-h-screen bg-graphite text-white flex items-center justify-center">
        <p>Page not found. <Link to="/" className="text-ember underline">Return home</Link>.</p>
      </div>
    );
  }

  return (
    <div data-testid={`legal-${slug}`}>
      <Seo title={`${page.title} | NOOR Design & Build`} description={`${page.title} for the NOOR Design & Build website (${settings?.website || "www.noordb.co.uk"}).`} path={`/${slug}`} />
      <section className="bg-graphite text-white blueprint-grid">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-14 md:pb-20">
          <Overline light>[ Legal ]</Overline>
          <h1 className="font-display font-light text-4xl sm:text-5xl tracking-tighter mt-6">{page.title}</h1>
          <p className="text-white/50 text-sm mt-6">Last updated: July 2026</p>
        </div>
      </section>
      <section className="bg-cream">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 py-14 md:py-20">
          {page.body.map(([h, t], i) => (
            <Reveal key={h}>
              <div className="py-8 border-t border-black/10 first:border-t-0">
                <h2 className="font-display font-medium text-xl">
                  <span className="font-mono text-xs text-ember mr-3">[ {String(i + 1).padStart(2, "0")} ]</span>{h}
                </h2>
                <p className="text-graphite/65 leading-relaxed mt-4 text-sm md:text-base">{t}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
